// src/contexts/PosContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { BASE_URL } from "../services/api"; // e.g. "http://localhost:5000/api"

const PosContext = createContext();

export const PosProvider = ({ children }) => {
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Memoize axios instance so it's stable across renders
  const api = useMemo(() => {
    return axios.create({
      baseURL: BASE_URL || "http://localhost:5000/api",
      headers: { "Content-Type": "application/json" },
    });
    // If BASE_URL can change during runtime, add it to deps:
    // }, [BASE_URL]);
  }, []);

  const fetchCounters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/api/poscounters/getPOSCounters");
      setCounters(res.data?.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Failed to fetch",
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await fetchCounters();
    })();
    return () => {
      mounted = false;
    };
    // fetchCounters is stable because api is memoized, so effect runs once
  }, [fetchCounters]);

  const getCounterById = useCallback(
    async (id) => {
      const res = await api.get(`/poscounters/getPOSCounterById/${id}`);
      return res.data?.data;
    },
    [api],
  );

  const createCounter = useCallback(
    async (payload) => {
      const res = await api.post("/poscounters/createPOSCounter", payload);
      setCounters((prev) => [res.data.data, ...prev]);
      return res.data.data;
    },
    [api],
  );

  const updateCounter = useCallback(
    async (id, payload) => {
      const res = await api.put(`/poscounters/updatePOSCounter/${id}`, payload);
      setCounters((prev) =>
        prev.map((c) => (c._id === id ? res.data.data : c)),
      );
      return res.data.data;
    },
    [api],
  );

  const deleteCounter = useCallback(
    async (id) => {
      await api.delete(`/poscounters/deletePOSCounter/${id}`);
      setCounters((prev) => prev.filter((c) => c._id !== id));
    },
    [api],
  );

  return (
    <PosContext.Provider
      value={{
        counters,
        loading,
        error,
        fetchCounters,
        getCounterById,
        createCounter,
        updateCounter,
        deleteCounter,
      }}
    >
      {children}
    </PosContext.Provider>
  );
};

export const usePos = () => {
  const ctx = useContext(PosContext);
  if (!ctx) throw new Error("usePos must be used inside PosProvider");
  return ctx;
};
